import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { CSSProperties, useCallback, useMemo, useRef, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';
import { Button, Loading, MessagePlugin } from 'tdesign-react';

import { ColorPickerRichMedia } from '@/components/colorPicker';
import { AddGrey, EditorDel, EditorDrag, Star3Grey } from '@/components/icon';
import { ImageRatio } from '@/const/richMedia';
import { getShortUuid } from '@/editor/flowEditor/control/utils/getNodeId';
import { UrlInfo } from '@/interface/aigc';
import { UserResourceType } from '@/interface/library';
import { DefaultCharacter, MultiCharacter, RichMediaValue } from '@/interface/media';
import AigcImageDialog from '@/material/AigcImageDialog';
import LibraryDialog, { ILibraryDialogRef } from '@/pages/library/dialog';
import { getColorsFromAi } from '@/services/color';

import { emitRichFilterMedia, emitRichMediaPIC, getRichLocalPath, transRichMediaValue } from '../change-data';
import SpineWebPlayer from '../core/player/spine';
import { ISpineResult } from '../folder';
import { IFileInfo, ImageRatioType } from '../image';
import ImageDialog from '../image/dialog';
import ImagePreviewAndEdit from '../image/previewAndEdit';
import { getBlur } from '../image/utils/getBlur';
import { getGrey } from '../image/utils/getGrey';
import SpineDialog from '../spine/dialog';
import SpinePreviewAndEdit from '../spine/previewAndEdit';
import styles from './index.module.less';

interface IProps {
  value: MultiCharacter[];
  onChange: (val: MultiCharacter[]) => any;
  disabled?: boolean;
  showSizeMsg?: string;
  limit?: {
    fileType?: string[];
    width?: number;
    height?: number;
    size?: number;
    showSizeMsg?: string;
    ratio?: ImageRatioType; // 宽高比
  };
  isHorizontal?: boolean;

  contentStyle?: CSSProperties;
  storyStyle?: string; // 故事风格
  filterBlur?: string;
  filterGrey?: string;
}

export const UploadCharacter: React.FC<IProps> = (props: IProps) => {
  const {
    value = [],
    limit = {},
    onChange,
    contentStyle,
    disabled = false,
    isHorizontal = false,
  } = props;

  const filterBlur = ''; // 目前不需要blur和grey图
  const filterGrey = '';
  const {
    ratio = ImageRatio['9:16'],
  } = limit;

  const [selectId, setSelectId] = useState<string | undefined>('');
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [colorLoading, setColorLoading] = useState(false);
  const [isCrop, setIsCrop] = useState(false); // 是否打开裁剪工具

  const colorRef = useRef<any>(null);

  const handleDrag = (val: MultiCharacter[]) => {
    onChange(val);
  };

  const removePic = (id?: string) => {
    const newValue = value?.filter(e => e.id !== id);
    onChange(newValue);
  };

  const imageValueChange = async (val: RichMediaValue) => {
    if (!val.id) return;
    const isNew = !value?.some(e => e.id === val?.id);
    const newItem: MultiCharacter = { ...DefaultCharacter, id: val.id, image: val };
    const newValue = isNew
      ? [...value, newItem]
      : value?.map(e => (e.id === val?.id ? { ...e, image: val } : e));
    const url = val?.url?.url;

    let sample = val?.sample;
    if (url && (!sample?.primary || !sample?.complementary)) {
      sample = await getColorSample(url);
    }

    if (url && (!sample?.primary || !sample?.complementary)) {
      MessagePlugin.error('未获取到图片颜色，请重新上传');
      emitRichMediaPIC(url);
      onChange(newValue);
      setUploadLoading(false);
      return false;
    }

    if (url) {
      let blurUrl = {};
      let greyUrl = {};
      if (filterBlur && !filterGrey) {
        blurUrl = await getBlur(url, val);
        if (!isEmpty(blurUrl)) {
          emitRichFilterMedia({
            [filterBlur]: { ...blurUrl, sample },
          });
        } else {
          MessagePlugin.error('图片上传处理失败，请重新上传');
          setUploadLoading(false);
          removePic(val?.id);
          return false;
        }
      }
      if (filterGrey && !filterBlur) {
        greyUrl = await getGrey(url, val);
        if (!isEmpty(greyUrl)) {
          emitRichFilterMedia({
            [filterGrey]: { ...greyUrl, sample },
          });
        } else {
          MessagePlugin.error('图片上传处理失败，请重新上传');
          removePic(val?.id);
          setUploadLoading(false);
          return false;
        }
      }
      if (filterBlur && filterGrey && filterGrey !== filterBlur) {
        blurUrl = await getBlur(url, val);
        greyUrl = await getGrey(url, val);
        if (!isEmpty(greyUrl) && !isEmpty(blurUrl)) {
          emitRichFilterMedia({
            [filterGrey]: { ...greyUrl, sample },
            // @ts-ignore：
            [filterBlur]: { ...blurUrl, sample },
          });
        } else {
          MessagePlugin.error('图片上传处理失败，请重新上传');
          removePic(val?.id);
          setUploadLoading(false);
          return false;
        }
      }
      onChange(newValue.map(e => (e.id === val.id
        ? { ...e, image: { ...val, sample } }
        : e)));
      emitRichMediaPIC(url);
    } else {
      onChange(newValue.filter(e => e.id !== val?.id));
    }
    setSelectId(val?.id);
    setUploadLoading(false);
  };

  // AI绘图
  const [aiDialogVisible, setAiDialogVisible] = useState<boolean>(false);

  const openAiDialog = () => {
    if (disabled) return;
    setAiDialogVisible(true);
  };

  const closeAiDialog = () => setAiDialogVisible(false);

  const handleAIconfirm = (urlInfo: UrlInfo) => {
    const { url, name } = urlInfo;
    const id = getShortUuid();
    const localPath = getRichLocalPath(url);
    const newChar: RichMediaValue = { id, url: { url, name, localPath } };
    if (url) {
      setUploadLoading(true);
      imageValueChange(newChar);
    }
    closeAiDialog();
  };

  // 素材库
  const imageLibraryRef = useRef<ILibraryDialogRef>(null);

  const confirmImageLibrary = (checkValue: any) => {
    const { url, name } = checkValue;
    const id = getShortUuid();
    const localPath = getRichLocalPath(url);
    const newChar: RichMediaValue = { id, url: { url, name, localPath } };
    if (url) {
      setUploadLoading(true);
      imageValueChange(newChar);
      imageLibraryRef.current?.close();
    }
  };

  // 图片编辑弹窗
  const openImageDialog = (id?: string) => {
    if (disabled || !id) return;
    setSelectId(id);
    setTimeout(() => setDialogVisible(true), 200); // 确保selectId已更新
  };

  const closeImageDialog = () => {
    setSelectId('');
    setIsCrop(false);
    setDialogVisible(false);
  };

  const onImageDialogConfirm = (imageInfo: IFileInfo) => {
    const { url, name } = imageInfo;
    const localPath = getRichLocalPath(url);
    imageValueChange({
      ...imageInfo,
      url: { url, name, localPath },
      showType: imageInfo.media?.showType,
    });
    setIsCrop(false);
    setDialogVisible(false);
  };

  // 取色
  const getColorSample = async (url: string) => {
    if (!url || disabled) return;

    const sample = await Promise.resolve(colorRef.current?.getColor?.(url)
      || getColorsFromAi(url).then(res => res.data));

    setColorLoading(false);
    const { primary, complementary } = sample;
    if (primary && complementary) {
      return { primary, complementary };
    }
    return undefined;
  };


  /*
   * Spine
  */
  const [spineDialogVisible, setSpineDialogVisible] = useState(false);

  const spineLibraryRef = useRef<ILibraryDialogRef>(null);

  const openSpineDialog = (id?: string) => {
    if (!id) return;
    setSelectId(id);
    setTimeout(() => setSpineDialogVisible(true), 200);
  };

  const closeSpineDialog = () => {
    setSpineDialogVisible(false);
  };

  const confirmSpineDialog = (val: ISpineResult) => {
    onSpineChange(val);
    closeSpineDialog();
  };

  const onSpineChange = (val: ISpineResult) => {
    onChange(value?.map(e => (e.id === val.id ? { ...e, spine: val } : e)));
  };

  const confirmSpineLibrary = (result: any) => {
    const id = getShortUuid();
    const newItem: MultiCharacter = { ...DefaultCharacter, id, spine: { id, ...result } };
    onChange([...value, newItem]);
    spineLibraryRef.current?.close();
  };

  // 操作按钮（上传、AI、素材库）
  const btnGroup = (style?: CSSProperties) => (
    <div
      className={styles.opBtnGroup}
      style={{ ...style }}
    >
      <div style={{ display: 'flex', gap: 3 }}>
        <Button
          theme='default'
          className={styles.opBtnCharacter}
          disabled={disabled}
          onClick={() => imageLibraryRef?.current?.open()}
        >
          <div className={styles.btnCharacterText}>
            <AddGrey />上传图片
          </div>
        </Button>
        <Button
          theme='default'
          className={styles.opBtnCharacter}
          disabled={disabled}
          onClick={() => spineLibraryRef?.current?.open()}
        >
          <div className={styles.btnCharacterText}>
            <AddGrey />上传 Spine
          </div>
        </Button>
      </div>
      <Button
        theme='default'
        className={styles.opBtnCharacter}
        disabled={disabled}
        onClick={openAiDialog}
      >
        <div className={styles.btnCharacterText}>
          <Star3Grey />AI 生成
        </div>
      </Button>
    </div>
  );

  // 多人立绘内样式
  const getCharacterDom = useCallback(() => (
    <div className={styles.character}>
      <div className={styles.display}>
        <ReactSortable
          disabled={disabled}
            list={value}
          setList={e => handleDrag(e)}
          className={styles.sortable}
          handle={'.draggable'}
        >
          {value?.map(e => <div key={e.id} className={styles.imageEdit}>
            <div className={classNames('draggable', styles.drag)}>
              <EditorDrag />
            </div>
            {e.spine
              ? <>
                <SpinePreviewAndEdit
                  value={e.spine}
                  disabled={disabled}
                  removeIcon={<EditorDel
                    style={{ width: 16, height: 16 }}
                    className={classNames({
                      ['nova-disabled-opacity']: disabled,
                      ['nova-disabled']: disabled,
                    })}
                  />}
                  openDialog={() => openSpineDialog(e.id)}
                  handleDelete={() => removePic(e.id)}
                />
                <div
                  key={`spine-thumbnail-${e.id}`}
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    backgroundColor: 'transparent',
                  }}>
                  <SpineWebPlayer
                    value={e.spine}
                    disabled={disabled}
                    config={{ width: '100px', height: '100px' }}
                    onChange={onSpineChange}
                  />
                </div>
              </>
              : <ImagePreviewAndEdit
                value={transRichMediaValue(e.image)}
                disabled={disabled}
                colorLoading={colorLoading}
                removeIcon={<EditorDel
                  style={{ width: 16, height: 16 }}
                  className={classNames({
                    ['nova-disabled-opacity']: disabled,
                    ['nova-disabled']: disabled,
                  })}
                />}
                openImageDialog={() => openImageDialog(e.id)}
                removePic={() => removePic(e.id)}
              />
            }
          </div>)}
        </ReactSortable>
      </div>
      <Loading
        loading={uploadLoading}
        showOverlay
        size='small'
        style={{
          paddingTop: 10,
          borderTop: '1px solid #FFFFFF14',
        }}
      >
          {btnGroup()}
      </Loading>
    </div>
  ), [value, disabled, uploadLoading, colorLoading]);

  // 点击对应item，打开编辑弹窗
  const selectItem: IFileInfo | ISpineResult = useMemo(
    () => {
      if (!selectId) return {};

      const res = value?.find(e => e.id === selectId);
      if (!res?.image && !res?.spine) return {};

      // Spine
      if (res?.spine) {
        return { ...res?.spine, id: res?.id };
      }

      // 图片
      const img = res?.image;
      return { ...img, ...img?.url, url: img?.url?.url, id: res?.id };
    }
    , [selectId, value],
  );

  return (
    <div style={{ ...contentStyle }} className={styles.container}>
      {getCharacterDom()}

      {/* 图片编辑弹窗 */}
      <ImageDialog
        visible={dialogVisible}
        imageInfo={selectItem}
        ratio={ratio}
        isCrop={isCrop}
        disabled={disabled}
        isHorizontal={isHorizontal}
        onClose={closeImageDialog}
        onConfirm={(v: IFileInfo) => onImageDialogConfirm(v)}
      />

      {/* AI 生成弹窗 */}
      <AigcImageDialog
        visible={aiDialogVisible}
        onClose={closeAiDialog}
        onConfirm={handleAIconfirm}
        data={{
          promptText: '',
          imageUrl: '',
          style: props.storyStyle,
        }}
        isHorizontal={isHorizontal}
      />

      {/* 图片素材库弹窗 */}
      <LibraryDialog
        dialogRef={imageLibraryRef}
        type={UserResourceType.RESOURCE_TYPE_IMAGE}
        confirmCallback={confirmImageLibrary}
      />

      {/* 取色组件 */}
      <ColorPickerRichMedia
        colorRef={colorRef}
        value={(selectItem as IFileInfo).sample}
        url={(selectItem as IFileInfo).url || ''}
        isShowColorPicker={false}
        getColorStatus={v => setColorLoading(v)}
        miniPicker={true}
        disabled={disabled}
      />

      {/* Spine预览弹窗 */}
      <SpineDialog
        value={selectItem}
        visible={spineDialogVisible}
        disabled={disabled}
        onClose={closeSpineDialog}
        onConfirm={confirmSpineDialog}
      />

      {/* Spine素材库弹窗 */}
      <LibraryDialog
        dialogRef={spineLibraryRef}
        type={UserResourceType.RESOURCE_TYPE_SPINE}
        confirmCallback={confirmSpineLibrary}
      />
    </div>
  );
};

export default UploadCharacter;
